/**
 * friendlyAuthError — map raw Supabase / network errors to human copy.
 * Falls back to a safe generic message rather than surfacing internal text.
 */
export function friendlyAuthError(err: unknown): string {
  const raw = extractMessage(err).toLowerCase();

  if (!raw) return "Something went wrong. Please try again.";

  // Supabase common codes
  if (raw.includes("invalid login credentials") || raw.includes("invalid_credentials")) {
    return "Incorrect email or password.";
  }
  if (raw.includes("email not confirmed") || raw.includes("email_not_confirmed")) {
    return "Please verify your email address first — check your inbox for the confirmation link.";
  }
  if (raw.includes("user already registered") || raw.includes("already registered") || raw.includes("user_already_exists")) {
    return "That email is already registered. Try signing in instead.";
  }
  if (raw.includes("weak password") || raw.includes("password should be at least")) {
    return "Password must be at least 8 characters.";
  }
  if (raw.includes("rate limit") || raw.includes("too many requests") || raw.includes("over_email_send_rate_limit")) {
    return "Too many attempts — please wait a moment and try again.";
  }
  if (raw.includes("otp_expired") || raw.includes("expired") || raw.includes("invalid or has expired")) {
    return "This link has expired or was already used. Please request a new one.";
  }
  if (raw.includes("network") || raw.includes("failed to fetch")) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (raw.includes("captcha")) {
    return "The security check failed. Please refresh and try again.";
  }

  // Stripe / payment common
  if (raw.includes("card_declined") || raw.includes("card was declined")) {
    return "Your card was declined. Please try a different card or contact your bank.";
  }
  if (raw.includes("insufficient_funds")) {
    return "Insufficient funds on this card.";
  }
  if (raw.includes("expired_card")) {
    return "That card has expired.";
  }
  if (raw.includes("payment intent") && (raw.includes("failed") || raw.includes("requires_payment_method"))) {
    return "Your payment didn't complete. Please try again.";
  }
  if (raw.includes("already") && (raw.includes("member") || raw.includes("subscribed") || raw.includes("active"))) {
    return "You're already a Member — welcome back.";
  }
  if (raw.includes("cancel") && raw.includes("payment")) {
    return "Payment cancelled. You haven't been charged.";
  }
  if (raw.includes("forbidden") || raw.includes("not authorized") || raw.includes("unauthorized")) {
    return "You need to be signed in to do that.";
  }


  // Fall back to the original message if it looks user-safe (short, no stack)
  const original = extractMessage(err);
  if (original && original.length < 160 && !original.includes("\n")) return original;
  return "Something went wrong. Please try again.";
}

function extractMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "";
}

/**
 * Text that means the message came from the database, transport layer or a
 * stack trace rather than from copy we wrote for members.
 */
const TECHNICAL_MARKERS = [
  "postgres", "pgrst", "supabase", "relation ", "column ", "constraint",
  "violates", "duplicate key", "sql", "jwt", "json", "syntax error",
  "unexpected token", "typeerror", "referenceerror", "undefined is not",
  "null value in", "row-level security", "permission denied for",
  "function public.", "schema cache", "econnrefused", "fetch failed",
  "status code", "http 5", "http 4", "stack", "at async", "    at ",
];

/**
 * friendlyError — the general-purpose version of `friendlyAuthError`.
 *
 * Known auth/payment/network cases get their mapped copy. Anything that reads
 * like machine output (database text, stack traces, HTTP noise) is replaced by
 * `fallback`, so a member never sees raw internals. Short, human-sounding
 * server messages are passed through unchanged.
 */
export function friendlyError(
  err: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  const raw = extractMessage(err);
  if (!raw) return fallback;

  const mapped = friendlyAuthError(err);
  const generic = "Something went wrong. Please try again.";
  if (mapped !== generic && mapped !== raw) return mapped;

  const lower = raw.toLowerCase();
  const technical =
    raw.length > 160 ||
    raw.includes("\n") ||
    raw.includes("{") ||
    TECHNICAL_MARKERS.some((marker) => lower.includes(marker));

  return technical ? fallback : raw;
}

/**
 * Saving reflections, check-ins and favourites to the account is a Membership
 * benefit, enforced in the database. When that check refuses the write we must
 * never show the raw policy text — members see plain copy instead.
 */
export const MEMBERSHIP_SAVE_TITLE = "Membership required";

export const MEMBERSHIP_SAVE_MESSAGE =
  "Membership required — saving to your account is part of Recovery Trauma™ Membership. Nothing is lost: this is still here on your device.";

export function isMembershipSaveBlock(err: unknown): boolean {
  const raw = extractMessage(err).toLowerCase();
  const code =
    typeof err === "object" && err !== null && "code" in err
      ? String((err as { code?: unknown }).code ?? "")
      : "";
  return (
    code === "42501" ||
    raw.includes("row-level security") ||
    raw.includes("row level security") ||
    raw.includes("violates policy") ||
    raw.includes("permission denied for") ||
    raw === MEMBERSHIP_SAVE_MESSAGE.toLowerCase()
  );
}

/** Friendly copy for a save that the database refused. */
export function friendlySaveError(err: unknown): string {
  if (isMembershipSaveBlock(err)) return MEMBERSHIP_SAVE_MESSAGE;
  return friendlyError(err, "We couldn't save that just now. Please try again.");
}
